import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CardActionArea from '@mui/material/CardActionArea';
import Stack from '@mui/material/Stack';
import { IRecentSearch } from '../../../api/types';
import { ITrackingPayload } from '../../../types/analytics';
import { ANALYTICS_CONSTANTS, buildModuleExperience, trackEDDL } from '../../../utilities/analytics';

interface handleTileClickProps {
    e: React.MouseEvent<HTMLAnchorElement>;
    title: string;
    inventoryCount: number;
    link: string;
}

const handleTileClick = ({ e, title, inventoryCount, link }: handleTileClickProps) => {
    e.preventDefault();

    const payload: ITrackingPayload = {
        event: ANALYTICS_CONSTANTS.EVENTS.CLICK.RECOMMENDED_CARTILE,
        linkDetails: {
            name: String(inventoryCount),
            position: title,
        },
        module: {
            moduleExperience: buildModuleExperience('VR|Homepage|Searched Tiles'),
        },
    };

    trackEDDL(payload);

    setTimeout(() => {
        window.location.href = link;
    }, ANALYTICS_CONSTANTS.TRACKING_DELAY);
};

export const ContinueSearchingCard = ({
    title,
    inventoryCount,
    inventoryCountText,
    newMatchCount,
    link,
}: Pick<IRecentSearch, 'title' | 'inventoryCount' | 'inventoryCountText' | 'newMatchCount' | 'link'>) => (
    <Card
        variant="outlined"
        aria-label={`Continue search for ${title}`}
        sx={({ palette }) => ({
            borderRadius: 1,
            '&:has(.Mui-focusVisible)': {
                outline: `0.125rem solid ${palette.primary.main}`,
                outlineOffset: '-0.125rem',
                background: palette.background.paper,
            },
            '.MuiCardActionArea-focusHighlight': {
                display: 'none',
            },
        })}
    >
        <CardActionArea href={link} onClick={(e) => handleTileClick({ e, title, inventoryCount, link })}>
            <Stack
                sx={() => ({
                    width: '11.75rem',
                    height: '4rem',
                    pt: 1.5,
                    pr: 1,
                    pb: 1,
                    pl: 1.5,
                })}
            >
                <Typography
                    variant="subtitle1"
                    sx={({ palette }) => ({
                        color: palette.text.primary,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                    })}
                >
                    {title}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                    <Typography
                        variant="body2"
                        sx={({ palette }) => ({
                            color: inventoryCount === 0 ? palette.error.main : palette.text.secondary,
                        })}
                    >
                        {inventoryCount === 0 ? 'No matches' : inventoryCountText}
                    </Typography>
                    {inventoryCount > 0 && newMatchCount > 0 && (
                        <Chip
                            color="flagged"
                            size="small"
                            label={`+${newMatchCount}`}
                            sx={{
                                padding: '0.1875rem 0.25rem',
                            }}
                            aria-label={`New matches: ${newMatchCount}`}
                        />
                    )}
                </Stack>
            </Stack>
        </CardActionArea>
    </Card>
);
